import { useCallback, useEffect, useRef } from 'react';

import { Form, FormControlProps } from 'react-bootstrap';

export interface ITextAreaProps extends FormControlProps {
  rows?: number;
  label?: string;
  placeholder?: string;
  containerClassName?: string;
  onBlur?: any;
  required?: boolean;
  value?: string;
  dataTestId?: string;
}

const TextArea = (props: ITextAreaProps) => {
  const { containerClassName, dataTestId, ...otherProps } = props;
  const textAreaRef = useRef(null);

  const setAutoHeightTextArea = useCallback(
    (value: string) => {
      const element: any = textAreaRef.current;

      // after setting the height to 'auto', the textarea's scrollHeight is automatically adjusted to the text's
      // height with a minimum of 2 lines of height
      element.style.height = 'auto';
      const scrollHeight = element.scrollHeight - 10;

      // count the number of line breaks in the text and calculate the height based on that count
      const [rowHeight, padding, rows, maxRows] = [24, 14, props.rows || 2, 10];
      const maxHeight = rowHeight * maxRows;
      const numberOfLineBreaks = (value.match(/\n/g) || []).length;
      const numberOfLine = numberOfLineBreaks + 1 > rows ? numberOfLineBreaks + 1 : rows;

      // set the height to the largest of the 2: scrollHeight adjusted to text or calculated height based on
      // the number of line breaks. Cap the height at 10 lines with maxHeight
      const areaHeight =
        Math.min(Math.max(scrollHeight, numberOfLine * rowHeight), maxHeight) + padding;

      element.style.height = `${areaHeight}px`;
    },
    [textAreaRef, props.rows]
  );

  useEffect(() => {
    setAutoHeightTextArea(props.value || '');
  }, [props.value, setAutoHeightTextArea]);

  return (
    <div className={containerClassName || ''}>
      {props.label && (
        <Form.Label data-test={dataTestId && `${dataTestId}-label`}>
          {props.label} {props.required && '*'}
        </Form.Label>
      )}
      <Form.Control
        data-test={dataTestId && `${dataTestId}-input`}
        ref={textAreaRef}
        type="text"
        as="textarea"
        custom
        {...otherProps} // all above can be overwritten by props but onChange
        className={`textArea ${props.className}`}
        onChange={(e: any) => props.onChange && props.onChange(e)}
      />
    </div>
  );
};

export default TextArea;
